import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ExternalLink } from 'lucide-react';
import { Button } from '@getro/rombo';
import moment from 'moment';
import { InfoBox } from '../../../components/atoms/InfoBox';
import { Rocket } from '../signupForm/rocket';

export const RunningState = ({ campaign, onOpenAgent }) => {
  const launchDate = campaign?.launchedAt
    ? moment(campaign.launchedAt).format('D MMM, YYYY')
    : moment(campaign?.createdAt).format('D MMM, YYYY');

  const stats = {
    signupsGenerated: campaign?.signupsGeneratedCount || 0,
    peopleContacted: campaign?.candidatesEmailedCount || 0,
    peopleToBeContacted: (campaign?.candidatesCount || 0) - (campaign?.candidatesEmailedCount || 0),
  };

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        height: '100%',
        px: '16px',
        py: '24px',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '24px',
          maxWidth: '800px',
          width: '100%',
          margin: '0 auto',
        }}
      >
        {/* Status Banner */}
        <Box
          sx={{
            borderRadius: '4px',
            border: '1px solid transparent',
            backgroundImage:
              'linear-gradient(white, white), linear-gradient(94deg, #FF9257 3.21%, #D400FF 63.82%, #4900FF 134.26%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
          }}
        >
          <Flex
            sx={{
              borderRadius: '4px',
              gap: '16px',
              p: '16px',
              background:
                'linear-gradient(251deg, rgba(73, 0, 255, 0.08) -5.07%, rgba(255, 68, 255, 0.08) 36.32%, rgba(255, 159, 138, 0.08) 107.1%), #FFF;',
            }}
          >
            <Rocket />
            <Flex sx={{ flexDirection: 'column', gap: '2px', flex: 1 }}>
              <Text sx={{ fontSize: '14px', fontWeight: '600', color: 'text.dark' }}>AI Agent is running</Text>
              <Text sx={{ fontSize: '12px' }}>Launched on {launchDate}</Text>
            </Flex>
            <Button variant="primary" size="small" icon={ExternalLink} iconGap="8px" onClick={onOpenAgent}>
              Open agent
            </Button>
          </Flex>
        </Box>

        {/* Stats Cards */}
        <Flex sx={{ gap: '16px', width: '100%' }}>
          <InfoBox
            count={stats.signupsGenerated}
            title="Signups generated"
            showCount
            infoText={<Text color="text.main">People who signed up to this list through agent outreach.</Text>}
            popoverPosition="top-end"
            popoverContentSx={{ width: '280px' }}
            sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'none' }}
          />
          <InfoBox
            count={stats.peopleContacted}
            title="People contacted"
            showCount
            infoText={<Text color="text.main">Total number of people the agent has reached out to.</Text>}
            popoverPosition="top-end"
            popoverContentSx={{ width: '280px' }}
            sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'none' }}
          />
          <InfoBox
            count={stats.peopleToBeContacted}
            title="People to be contacted"
            showCount
            infoText={<Text color="text.main">Remaining people in the outreach queue.</Text>}
            popoverPosition="top-end"
            popoverContentSx={{ width: '280px' }}
            sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'none' }}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

RunningState.propTypes = {
  campaign: PropTypes.shape({
    launchedAt: PropTypes.string,
    createdAt: PropTypes.string,
    signupsGeneratedCount: PropTypes.number,
    candidatesEmailedCount: PropTypes.number,
    candidatesRemainingCount: PropTypes.number,
    candidatesCount: PropTypes.number,
  }),
  onOpenAgent: PropTypes.func.isRequired,
};

RunningState.defaultProps = {
  campaign: null,
};
